/** Summarize connector transfers without creating a second analysis workflow. */
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Loader2, Play } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { NodeApiClient } from '@/app-shell/services'
import { Button } from '@/components/ui'
import { listConnectorAnalyses } from '../api'
import { connectorDefinition } from '../registry'

const activeStates = new Set(['syncing', 'running', 'disconnecting'])

export function ConnectorSyncStrip({
  api,
  orgId,
  workspaceId,
  pending,
}: {
  api: NodeApiClient
  orgId: string
  workspaceId: string
  pending?: {
    count: number
    singular: string
    plural: string
    running?: boolean
    onRun: () => void
  }
}) {
  const analyses = useQuery({
    queryKey: ['connector-analyses', orgId, workspaceId],
    queryFn: () => listConnectorAnalyses(api, orgId, workspaceId),
    enabled: Boolean(orgId && workspaceId),
    refetchInterval: query => query.state.data?.some(item => activeStates.has(item.state)) ? 5_000 : 30_000,
  })

  if (!analyses.data?.length) return null

  return (
    <section className="flex flex-wrap items-center gap-x-[18px] gap-y-2 rounded-[10px] border border-slate-200 bg-white px-4 py-2.5" data-testid="connector-sync-strip">
      <h2 className="text-xs font-semibold tracking-[0.02em] text-slate-500">CONNECTOR SYNC</h2>
      {analyses.data.map(analysis => {
        const definition = connectorDefinition(analysis.provider)
        const empty = analysis.state === 'empty'
        const sourceRecordLabel = analysis.sourceRecords === 1
          ? analysis.sourceRecordUnit.singular
          : analysis.sourceRecordUnit.plural
        return (
          <span
            key={analysis.feedId}
            className="flex flex-wrap items-center gap-[7px] text-[12.5px] text-slate-700"
            data-testid={`connector-sync-status-${analysis.feedId}`}
          >
            <span className={`h-[7px] w-[7px] shrink-0 rounded-full ${empty ? 'bg-slate-300' : activeStates.has(analysis.state) ? 'animate-pulse bg-blue-500' : 'bg-green-500'}`} />
            <strong className="font-semibold">{definition.label}</strong>
            <span>{empty
              ? 'nothing synced yet'
              : `${analysis.sourceRecords.toLocaleString()} ${sourceRecordLabel} synced · ready to run on the dashboard`}</span>
          </span>
        )
      })}
      <div className="ml-auto flex flex-wrap items-center gap-3">
        {pending && (
          <>
            <span
              className="text-[12.5px] font-medium text-slate-600"
              data-testid="connector-sync-pending"
            >
              {pending.count.toLocaleString()} {pending.count === 1 ? pending.singular : pending.plural} pending
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={pending.count === 0 || pending.running}
              onClick={pending.onRun}
              data-testid="connector-sync-run-pending"
            >
              {pending.running
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Play className="h-4 w-4" />}
              {pending.running ? 'Running…' : 'Run all pending'}
            </Button>
          </>
        )}
        <Link
          to="/workspace-connectors"
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-slate-800 hover:text-slate-600"
          data-testid="connector-sync-manage-link"
        >
          Manage connectors <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </section>
  )
}
